import { CollapsableCard } from '@app/app/_components/CollapsableCard/CollapsableCard';
import { CheckboxInput } from '@app/app/_components/Input/CheckboxInput';
import { Paragraph } from '@app/app/_components/Paragraph/Paragraph';
import { ViolationMap } from '@app/lib/form/contracts/ViolationMap';
import { useTranslations } from 'next-intl';
import { TextButton } from '@app/app/_components/TextButton/TextButton';
import { useCallback, useState } from 'react';
import { downloadLifeOffer } from '@app/service/eshop/life/actions/downloadLifeOffer';
import { logger } from '@app/infrastructure/logger/logger';
import { AuthState } from '@app/infrastructure/auth/model/AuthState';
import { Heading } from '@app/app/_components/Heading/Heading';
import { ArrowRightIcon } from '@app/app/_icons/ArrowRightIcon';
import { base64ToBlob } from '@app/infrastructure/application/base64ToBlob';
import { SpinnerIcon } from '@app/app/_icons/SpinnerIcon';

interface Props {
    violations?: ViolationMap;
    offerPdfIdentifier: string;
    authState: AuthState;
}

export function ConsentsStep({ violations, offerPdfIdentifier, authState }: Props) {
    const t = useTranslations();
    const [isDownloading, setIsDownloading] = useState(false);

    const confirmationItems = Array.from({ length: 9 }, (_, index) => index + 1);

    const handleDownloadPdf = useCallback(async () => {
        if (!offerPdfIdentifier) return;

        const newWindow = window.open('about:blank', '_blank');

        setIsDownloading(true);

        try {
            const base64Data = await downloadLifeOffer(
                offerPdfIdentifier,
                authState?.selectedClient?.personalCode ||
                    authState?.profile?.personalCode ||
                    authState?.personalCode ||
                    '',
            );

            if (!base64Data) {
                newWindow?.close?.();
                return;
            }

            const blob = base64ToBlob(base64Data, 'application/pdf');
            const previewUrl = URL.createObjectURL(blob);

            if (newWindow) {
                newWindow.location.href = previewUrl;
                newWindow.addEventListener?.('pagehide', () => URL.revokeObjectURL(previewUrl));
            } else {
                window.location.href = previewUrl;
            }
        } catch (error) {
            logger.logException(error);
        } finally {
            setIsDownloading(false);
        }
    }, [
        offerPdfIdentifier,
        authState?.selectedClient?.personalCode,
        authState?.profile?.personalCode,
        authState?.personalCode,
    ]);

    return (
        <div className="mb-20 flex w-full flex-col gap-6">
            <CollapsableCard defaultCollapsed={false} className="p-6" label={t('eshop.life.confirmationsTitle')}>
                <ul className="mb-4 list-outside list-disc space-y-1 px-6">
                    {confirmationItems.map(number => (
                        <li
                            key={number}
                            dangerouslySetInnerHTML={{ __html: t.raw(`eshop.life.confirmations.item${number}`) }}
                        />
                    ))}
                </ul>
                <CheckboxInput
                    error={violations?.agreedToConfirmations}
                    name="agreedToConfirmations"
                    label={t('eshop.life.iConfirm')}
                />
            </CollapsableCard>
            <CollapsableCard defaultCollapsed={false} className="p-6" label={t('eshop.life.marketingConsentsTitle')}>
                <div className="flex flex-col gap-4">
                    <Paragraph>{t('eshop.life.marketingConsents.marketingOffers')}</Paragraph>
                    <CheckboxInput name="agreedToMarketingOffers" label={t('eshop.life.iConfirm')} />
                    <Paragraph>{t('eshop.life.marketingConsents.seesamMarketingOffers')}</Paragraph>
                    <CheckboxInput name="agreedToSeesamMarketingOffers" label={t('eshop.life.iConfirm')} />
                    <div dangerouslySetInnerHTML={{ __html: t.raw('eshop.life.marketingConsents.youCanWithdraw') }} />
                </div>
            </CollapsableCard>

            {offerPdfIdentifier && (
                <div className="flex flex-col gap-4">
                    <Heading level="h3">{t('eshop.life.offer')}</Heading>
                    <Paragraph>{t('eshop.life.offerDownloadDescription')}</Paragraph>
                    <TextButton
                        onClick={handleDownloadPdf}
                        rightAddon={<ArrowRightIcon className="size-4" />}
                        className="underline underline-offset-2"
                        preventDefault>
                        {isDownloading ? (
                            <div className="flex gap-2">
                                {t('eshop.life.downloading')}
                                <SpinnerIcon className="w-5 animate-spin text-primary-500" />
                            </div>
                        ) : (
                            t('eshop.life.downloadPdfOffer')
                        )}
                    </TextButton>
                </div>
            )}
        </div>
    );
}
